'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/lib/toast';
import { ApiClientError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DocumentEditor, type DocumentEditorRef } from '@/features/document-editor';
import {
  createReleaseNote,
  getReleaseNotes,
  type ReleaseNote,
} from '@/services/api/devtracker';
import { colors, typography } from '@/styles/theme';

export default function DevtrackerUpdatesPage() {
  const { t, locale } = useTranslation();
  const { isReady } = useAuthGuard();
  const { hasPermission, isAdmin } = usePermissions();
  const editorRef = useRef<DocumentEditorRef>(null);
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [formState, setFormState] = useState({
    version: '',
    title: '',
    releasedAt: new Date().toISOString().slice(0, 10),
  });

  const canManage = isAdmin || hasPermission('devtracker:manage');

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getReleaseNotes({ page: 1, pageSize: 50 });
      setNotes(result.items);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.devtracker?.toast?.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  }, [t.devtracker?.toast?.loadFailed]);

  useEffect(() => {
    if (!isReady) return;
    loadNotes();
  }, [isReady, loadNotes]);

  const formatDate = (value: string) => {
    return new Date(value).toLocaleDateString(locale);
  };

  const handleCreate = async () => {
    if (!formState.version.trim() || !formState.title.trim() || !formState.releasedAt) {
      toast.error(t.devtracker?.toast?.requiredFields);
      return;
    }

    try {
      setSaving(true);
      const contentPayload = editorRef.current?.isEmpty()
        ? undefined
        : ((editorRef.current?.getJSON() ?? {}) as Record<string, unknown>);
      const payload = {
        version: formState.version.trim(),
        title: formState.title.trim(),
        releasedAt: new Date(formState.releasedAt).toISOString(),
        content: contentPayload,
      };
      await createReleaseNote(payload);
      toast.success(t.devtracker?.toast?.releaseNoteCreated);
      setFormState({
        version: '',
        title: '',
        releasedAt: new Date().toISOString().slice(0, 10),
      });
      setContent('');
      editorRef.current?.setContent({ type: 'doc', content: [{ type: 'paragraph' }] });
      loadNotes();
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.devtracker?.toast?.releaseNoteCreateFailed);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isReady) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
        {t.devtracker?.loading}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <h1 className={typography.h4}>{t.devtracker?.updates?.title}</h1>
          {canManage && (
            <Button size="sm" onClick={handleCreate} disabled={saving}>
              {saving ? t.devtracker?.create?.saving : t.devtracker?.updates?.actions?.save}
            </Button>
          )}
        </div>
      </div>

      <div className="px-6">
        <p className="text-sm text-gray-500">{t.devtracker?.updates?.subtitle}</p>
      </div>

      <div className="px-6 space-y-4">
        {canManage && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-gray-900">{t.devtracker?.updates?.formTitle}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="text-sm text-gray-600">{t.devtracker?.updates?.fields?.version}</label>
                  <Input
                    value={formState.version}
                    onChange={(event) => setFormState((prev) => ({ ...prev, version: event.target.value }))}
                    placeholder={t.devtracker?.updates?.placeholders?.version}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-600">{t.devtracker?.updates?.fields?.title}</label>
                  <Input
                    value={formState.title}
                    onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder={t.devtracker?.updates?.placeholders?.title}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="text-sm text-gray-600">{t.devtracker?.updates?.fields?.releasedAt}</label>
                  <Input
                    type="date"
                    value={formState.releasedAt}
                    onChange={(event) => setFormState((prev) => ({ ...prev, releasedAt: event.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-600">{t.devtracker?.updates?.fields?.content}</label>
                <DocumentEditor
                  ref={editorRef}
                  value={content}
                  placeholder={t.devtracker?.create?.contentHint}
                  onChange={setContent}
                  minHeight="240px"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleCreate} disabled={saving}>
                  {saving ? t.devtracker?.create?.saving : t.devtracker?.updates?.actions?.save}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center text-gray-400">{t.devtracker?.loading}</div>
        ) : notes.length === 0 ? (
          <div className="text-center text-gray-400">{t.devtracker?.updates?.empty}</div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <Card key={note.id}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base text-gray-900">
                      {note.title}
                    </CardTitle>
                    <div className="text-xs text-gray-500">
                      {note.version} · {formatDate(note.releasedAt)}
                    </div>
                  </div>
                </CardHeader>
                {note.content ? (
                  <CardContent>
                      <DocumentEditor
                        value={note.content ? JSON.stringify(note.content) : undefined}
                        readOnly
                        minHeight="160px"
                      />
                  </CardContent>
                ) : null}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
